import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { isNative } from "@/lib/native/platform";

const ResetPassword = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const [isForced, setIsForced] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
      }
    });

    const initializeRecovery = async () => {
      const params = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));

      if (params.get('forced') === 'true') {
        setIsForced(true);
        setIsRecovery(true);
      }

      // Native: the recovery email's redirect target is getOAuthRedirect("reset-password")
      // (cbam:// deep link, set in PartnerLogin.handleForgotPassword). The appUrlOpen dispatcher
      // (deepLinks.ts) already exchanged the PKCE code for a session and navigated here, so there
      // is no `code`/`token_hash` in the URL to consume — mark recovery active and skip URL parsing.
      // The PASSWORD_RECOVERY auth event above also covers this. Web behaviour is unchanged.
      if (isNative()) {
        setIsRecovery(true);
        return;
      }

      if (hashParams.get('type') === 'recovery') {
        setIsRecovery(true);
        return;
      }

      const code = params.get('code');
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (!error) {
          setIsRecovery(true);
          return;
        }
      }

      const tokenHash = params.get('token_hash');
      if (tokenHash && params.get('type') === 'recovery') {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        });

        if (!error) {
          setIsRecovery(true);
        }
      }
    };

    void initializeRecovery();

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) throw error;

      // Clear the force_password_change flag
      if (isForced) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('profiles')
            .update({ force_password_change: false })
            .eq('user_id', user.id);
        }
      }

      toast({
        title: "Password Updated",
        description: "Your password has been updated successfully.",
      });
      navigate("/partner/login", { replace: true });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Could not update password.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = async () => {
    // Sign out any session that may have been created during recovery flow
    // to prevent bypassing password reset by navigating to login
    await supabase.auth.signOut();
    navigate("/partner/login", { replace: true });
  };

  if (!isRecovery) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">
              This page is only accessible via a password reset link. Please check your email.
            </p>
            <Button className="mt-4" onClick={handleBackToLogin}>
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center py-12 px-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>{isForced ? "Password Change Required" : "Reset Password"}</CardTitle>
          <CardDescription>
            {isForced 
              ? "Your password was reset by an administrator. Please set a new password to continue."
              : "Enter your new password below."
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
